/**
 * Monthly standings (docs/bounties.md "Monthly standings"): the Peon of the Month card's gold,
 * silver and bronze rows under the winner. Pure copy and visibility rules, shared by the web and the
 * native app so the two cards can never disagree.
 */
import type { TopPeon } from "./types";

/** The medal each place wears, gold first. */
export const STANDING_MEDALS = ["gold", "silver", "bronze"] as const;
export type StandingMedal = (typeof STANDING_MEDALS)[number];

/** The row's unit after its number: "task" for one, "tasks" otherwise. */
export function standingUnit(count: number): string {
  return count === 1 ? "task" : "tasks";
}

/** The row's count: "14 tasks", "1 task". */
export function standingCount(count: number): string {
  return `${count} ${standingUnit(count)}`;
}

/**
 * Whether the card shows the list area under the divider: whenever the flag is on. One peon this
 * month is one row (gold); none is the empty line (`standingsEmptyLine`) in the list's place, so the
 * leaderboard never vanishes when the month turns (owner, 2026-10-01: "The leaderboard is no longer
 * showing"). Flag off, and the card is the crown alone, as before the flag. The list itself comes
 * from `standingRows`; an absent list (a board remembered before the server sent standings) reads as
 * empty. `crownNamed` is whether the header names anyone (last month's winner or this month's
 * leader); with nobody there either, the empty line is skipped.
 */
export function showStandings(flagOn: boolean, standings?: readonly TopPeon[] | null, crownNamed = true): boolean {
  // A brand-new household with nobody this month or last: the header already says the crown is up
  // for grabs, so the empty line would say the same thing twice. The list area waits for a row.
  return flagOn && ((standings?.length ?? 0) > 0 || crownNamed);
}

/** The muted line in the list's place when nobody has completed a task yet this month. */
export function standingsEmptyLine(): string {
  return "Nobody has finished a task yet this month.";
}

export interface StandingRow {
  medal: StandingMedal;
  name: string;
  /** "14", drawn right-aligned so the numbers line up. */
  number: string;
  /** "tasks" or "task", drawn after the number. */
  unit: string;
  /** "Gold, Omie, 14 tasks": the row read aloud, rank in words as well as colour and order. */
  label: string;
}

/** The rows as drawn, at most three, gold first; none for an empty or absent list. */
export function standingRows(standings: readonly TopPeon[] | null | undefined): StandingRow[] {
  return (standings ?? []).slice(0, STANDING_MEDALS.length).map((peon, i) => {
    const medal = STANDING_MEDALS[i];
    return {
      medal,
      name: peon.name,
      number: String(peon.count),
      unit: standingUnit(peon.count),
      label: `${medal[0].toUpperCase()}${medal.slice(1)}, ${peon.name}, ${standingCount(peon.count)}`,
    };
  });
}
